import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BookOpen, Plus, Users, Calendar, Video, Search } from "lucide-react";

const CommunityStudyGroups = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("all");

  const [groupForm, setGroupForm] = useState({
    name: "",
    description: "",
    subject: "Mathematics",
    max_members: 10,
    schedule: "",
    meeting_link: ""
  });

  const subjects = ["All", "Mathematics", "Science", "English", "Programming", "History", "Geography"];

  useEffect(() => {
    fetchGroups();
  }, [selectedSubject]);

  const fetchGroups = async () => {
    try {
      const params = selectedSubject !== "all" ? `?subject=${selectedSubject}` : "";
      const response = await axios.get(`${API}/community/study-groups${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroups(response.data);
    } catch (error) {
      toast.error("Failed to load groups");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    try {
      const form = new FormData();
      form.append("name", groupForm.name);
      form.append("description", groupForm.description);
      form.append("subject", groupForm.subject);
      form.append("max_members", groupForm.max_members);
      if (groupForm.schedule) form.append("schedule", groupForm.schedule);
      if (groupForm.meeting_link) form.append("meeting_link", groupForm.meeting_link);

      await axios.post(`${API}/community/study-groups`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Study group created!");
      setShowCreateModal(false);
      setGroupForm({ name: "", description: "", subject: "Mathematics", max_members: 10, schedule: "", meeting_link: "" });
      fetchGroups();
    } catch (error) {
      toast.error("Failed to create group");
    }
  };

  const handleJoinGroup = async (groupId) => {
    try {
      await axios.post(`${API}/community/study-groups/${groupId}/join`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Joined group successfully!");
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join group");
    }
  };

  const handleLeaveGroup = async (groupId) => {
    try {
      await axios.post(`${API}/community/study-groups/${groupId}/leave`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Left group successfully!");
      fetchGroups();
    } catch (error) {
      toast.error("Failed to leave group");
    }
  };

  const filteredGroups = groups.filter(group =>
    (group.name && group.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (group.description && group.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading groups...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-md">
                <BookOpen className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Study Groups</h1>
                <p className="text-gray-600 text-sm md:text-base">Learn together with peers</p>
              </div>
            </div>
            <Button onClick={() => setShowCreateModal(true)} className="shadow-md">
              <Plus className="w-4 h-4 mr-2" />
              Create Group
            </Button>
          </div>
        </div>

        {/* Search and Filter */}
        <Card className="mb-6 border-none shadow-md">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
                <Input
                  placeholder="Search groups..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                {subjects.map(subj => (
                  <Button
                    key={subj}
                    variant={selectedSubject === subj.toLowerCase() ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedSubject(subj.toLowerCase())}
                  >
                    {subj}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Groups Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredGroups.length > 0 ? (
            filteredGroups.map(group => {
              const isMember = group.members.includes(user.id);
              const isFull = group.members.length >= group.max_members;
              
              return (
                <Card key={group.id} className="border-none shadow-md hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between mb-2">
                      <Badge variant="outline">{group.subject}</Badge>
                      <Badge variant="secondary" className="text-xs">
                        <Users className="w-3 h-3 mr-1" />
                        {group.members.length}/{group.max_members}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg">{group.name}</CardTitle>
                    <CardDescription className="text-sm line-clamp-2">
                      {group.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {group.schedule && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                        <Calendar className="w-4 h-4" />
                        <span>{group.schedule}</span>
                      </div>
                    )}
                    
                    {group.meeting_link && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                        <Video className="w-4 h-4" />
                        <span className="truncate">Meeting link available</span>
                      </div>
                    )}
                    
                    {isMember ? (
                      <div className="space-y-2">
                        <Badge className="w-full justify-center bg-green-100 text-green-700 border-green-200">
                          You're a member
                        </Badge>
                        {group.meeting_link && (
                          <Button
                            onClick={() => window.open(group.meeting_link, '_blank')}
                            variant="outline"
                            size="sm"
                            className="w-full"
                          >
                            <Video className="w-4 h-4 mr-2" />
                            Join Meeting
                          </Button>
                        )}
                        <Button
                          onClick={() => handleLeaveGroup(group.id)}
                          variant="ghost"
                          size="sm"
                          className="w-full text-destructive hover:text-destructive"
                        >
                          Leave Group
                        </Button>
                      </div>
                    ) : (
                      <Button
                        onClick={() => handleJoinGroup(group.id)}
                        className="w-full"
                        size="sm"
                        disabled={isFull}
                      >
                        {isFull ? "Group Full" : "Join Group"}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <div className="col-span-full">
              <Card className="border-none shadow-md">
                <CardContent className="p-12 text-center">
                  <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No groups found</h3>
                  <p className="text-muted-foreground mb-4">
                    {searchTerm ? "Try adjusting your search" : "Be the first to create a study group!"}
                  </p>
                  <Button onClick={() => setShowCreateModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Group
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Create Group Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Study Group</DialogTitle>
              <DialogDescription>Start a group to study together</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Group Name</Label>
                <Input
                  value={groupForm.name}
                  onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                  placeholder="e.g., Advanced Mathematics Study Group"
                />
              </div>
              <div>
                <Label>Subject</Label>
                <Input
                  value={groupForm.subject}
                  onChange={(e) => setGroupForm({ ...groupForm, subject: e.target.value })}
                  placeholder="e.g., Mathematics"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={groupForm.description}
                  onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                  placeholder="Describe the group's purpose and goals..."
                  rows={3}
                />
              </div>
              <div>
                <Label>Maximum Members</Label>
                <Input
                  type="number"
                  min="2"
                  max="50"
                  value={groupForm.max_members}
                  onChange={(e) => setGroupForm({ ...groupForm, max_members: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <Label>Schedule (optional)</Label>
                <Input
                  value={groupForm.schedule}
                  onChange={(e) => setGroupForm({ ...groupForm, schedule: e.target.value })}
                  placeholder="e.g., Every Monday and Wednesday, 7 PM"
                />
              </div>
              <div>
                <Label>Meeting Link (optional)</Label>
                <Input
                  value={groupForm.meeting_link}
                  onChange={(e) => setGroupForm({ ...groupForm, meeting_link: e.target.value })}
                  placeholder="e.g., https://meet.google.com/xxx-yyyy-zzz"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
              <Button onClick={handleCreateGroup}>Create Group</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Footer />
    </div>
  );
};

export default CommunityStudyGroups;
